import { useEffect, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { MoneyFoxLogo } from '@/shared/components/moneyfox-logo';
import { useQuiz } from '@/features/quiz';
import { trackEvent } from '@/features/analytics';
import { warmPayrails } from '@/features/payments';
import { cn } from '@/lib/utils';
import { usePreloadQuizImages } from '../hooks/use-preload-quiz-images';
import quizBg from '../assets/quiz-bg.webp';
import type { QuizConfig } from '../types';
import { SingleChoiceStep } from './steps/single-choice-step';
import { MultipleChoiceStep } from './steps/multiple-choice-step';
import { InputStep } from './steps/input-step';
import { EmailStep } from './steps/email-step';
import { InfoStep } from './steps/info-step';
import { ProcessingStep } from './steps/processing-step';
import { ClaimsStep, ProfileStep, SigningStep, prefetchSettlements } from '@/features/claims';

const DEFAULT_STEP_IMAGE_CLASS =
  'h-auto max-h-[176px] w-auto max-w-full object-contain';

const PRELOAD_AT_STEP = 2;

interface QuizRendererProps {
  config: QuizConfig;
  onComplete: () => void;
}

export function QuizRenderer({ config, onComplete }: QuizRendererProps) {
  const {
    currentStep,
    currentStepIndex,
    currentValue,
    countedCurrent,
    countedTotal,
    showProgressCounter,
    progress,
    isFirst,
    canProceed,
    goNext,
    goBack,
    setCurrentAnswer,
  } = useQuiz(config);

  const [visible, setVisible] = useState(true);
  const prevStepId = useRef(currentStep?.id);
  const firedShowFor = useRef<string | undefined>(undefined);

  // Idempotent if already warmed during onboarding; covers direct /quiz entry.
  usePreloadQuizImages();

  useEffect(() => {
    if (currentStep && currentStep.id !== prevStepId.current) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      setVisible(false);
      const t = requestAnimationFrame(() => {
        prevStepId.current = currentStep.id;
        setVisible(true);
      });
      return () => cancelAnimationFrame(t);
    }
  }, [currentStep]);

  // Named "shown" event (data-driven), once per step. The ref guard keeps it
  // idempotent across Strict-Mode's double-invoke and unrelated re-renders.
  useEffect(() => {
    if (currentStep?.eventOnShow && firedShowFor.current !== currentStep.id) {
      firedShowFor.current = currentStep.id;
      trackEvent(currentStep.eventOnShow);
    }
  }, [currentStep]);

  useEffect(() => {
    if (currentStepIndex >= PRELOAD_AT_STEP) {
      warmPayrails().catch(() => {});
    }
  }, [currentStepIndex]);

  useEffect(() => {
    if (currentStep?.type === 'email' || currentStep?.type === 'processing') {
      prefetchSettlements().catch(() => {});
    }
  }, [currentStep?.type]);

  if (!currentStep) return null;

  const handleNext = () => {
    const completed = goNext();
    if (completed) onComplete();
  };

  const handleSingleChoice = (value: string) => {
    setCurrentAnswer(value);
    setTimeout(() => {
      const completed = goNext();
      if (completed) onComplete();
    }, 200);
  };

  if (currentStep.fullBleed || currentStep.type === 'processing') {
    return (
      <div
        key={currentStep.id}
        className="min-h-[100dvh] transition-opacity duration-200"
        style={{ opacity: visible ? 1 : 0 }}
      >
        {currentStep.type === 'processing' && <ProcessingStep onAdvance={handleNext} />}
        {currentStep.type === 'email' && (
          <EmailStep
            step={currentStep}
            value={typeof currentValue === 'string' ? currentValue : ''}
            onChange={setCurrentAnswer}
            onAdvance={handleNext}
          />
        )}
        {currentStep.type === 'claims' && (
          <ClaimsStep onBack={goBack} onAdvance={handleNext} />
        )}
        {currentStep.type === 'profile' && (
          <ProfileStep onBack={goBack} onAdvance={handleNext} />
        )}
        {currentStep.type === 'signing' && (
          <SigningStep onBack={goBack} onAdvance={handleNext} />
        )}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[100dvh] flex-col overflow-hidden bg-mf-neutral-50 font-body text-mf-neutral-900">
      <img
        src={quizBg}
        alt=""
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-1/2 h-auto w-[140%] max-w-none -translate-x-1/2 select-none opacity-90"
      />

      <div className="relative z-10 flex flex-col gap-3 px-4 pt-3">
        <div className="relative flex items-center justify-between">
          <button
            type="button"
            onClick={goBack}
            disabled={isFirst}
            aria-label="Back"
            className="flex size-9 items-center justify-center rounded-full bg-white shadow-[0_0_16px_rgba(26,22,19,0.03)] disabled:pointer-events-none disabled:opacity-40"
          >
            <ChevronLeft className="size-5 text-mf-neutral-900" strokeWidth={1.75} />
          </button>
          <MoneyFoxLogo
            size="sm"
            className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          />
          {showProgressCounter ? (
            <div className="rounded-full border border-mf-neutral-200 px-4 py-2">
              <span className="font-body text-sm font-medium leading-4 text-mf-neutral-900">
                {countedCurrent} / {countedTotal}
              </span>
            </div>
          ) : (
            <span className="size-9 shrink-0" aria-hidden />
          )}
        </div>
        {showProgressCounter && (
          <div className="h-1 w-full overflow-hidden rounded-sm bg-mf-neutral-100">
            <div
              className="h-full rounded-sm bg-mf-green transition-all duration-300"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        )}
      </div>

      <div className="relative z-10 flex flex-1 flex-col px-4 pb-4 pt-4">
        <div
          key={currentStep.id}
          className="flex flex-1 flex-col transition-all duration-200 ease-out"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(16px)',
          }}
        >
          <div className="flex flex-1 flex-col items-center justify-center gap-6">
            <div className="flex w-full flex-col items-center gap-0">
              {currentStep.image && (
                <img
                  src={currentStep.image}
                  alt=""
                  className={cn(
                    'object-contain',
                    currentStep.imageClassName ?? DEFAULT_STEP_IMAGE_CLASS,
                  )}
                />
              )}
              <div className="mt-0 w-full text-center">
                <h1 className="font-display text-xl font-bold leading-6 text-mf-neutral-900">
                  {currentStep.title}
                </h1>
                {currentStep.subtitle && currentStep.type !== 'info' && (
                  <p className="mt-1 font-body text-base font-normal leading-5 text-mf-neutral-600">
                    {currentStep.subtitle}
                  </p>
                )}
              </div>
            </div>

            <div className="w-full">
              {currentStep.type === 'single-choice' && (
                <SingleChoiceStep
                  step={currentStep}
                  value={typeof currentValue === 'string' ? currentValue : undefined}
                  onChange={handleSingleChoice}
                />
              )}
              {currentStep.type === 'multiple-choice' && (
                <MultipleChoiceStep
                  step={currentStep}
                  value={Array.isArray(currentValue) ? currentValue : []}
                  onChange={setCurrentAnswer}
                />
              )}
              {currentStep.type === 'input' && (
                <InputStep
                  step={currentStep}
                  value={typeof currentValue === 'string' ? currentValue : ''}
                  onChange={setCurrentAnswer}
                />
              )}
              {currentStep.type === 'info' && <InfoStep step={currentStep} />}
            </div>
          </div>

          {currentStep.type !== 'single-choice' && (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canProceed}
              className="mt-4 flex h-12 w-full items-center justify-center rounded-xl bg-mf-green text-base font-semibold text-white transition-colors hover:bg-mf-green-dark disabled:opacity-40"
            >
              {currentStep.ctaLabel || 'Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
